import { Button, mediaQueries, style, Box } from '@do/walrus';
import React, { ReactElement } from 'react';
import styled from 'styled-components';
import { DynamicVariantProps, Sizes } from '../interfaces/variant';
import { Column, WrapSplit } from './Common';
import { trackEvent } from '@do/seashell';

interface VariantCopyProps {
  color?: string;
  sizeMobile?: string;
  sizeDesktop?: string;
}
type Props = Pick<DynamicVariantProps, 'splash'> & {
  variantType: string;
};

const Wrap = styled.div<{ paddingX?: string }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  z-index: ${style.vars.zIndex.base};
  text-align: left;
  width: 100%;
  max-width: 720px;
  margin-top: 70px;
  padding: 0 ${style.vars.whitespace.regular};
  ${mediaQueries.mediumMin} {
    margin-top: 115px;
    padding: ${({ paddingX }) => (paddingX ? `0 ${paddingX}` : '0 128px')};
  }
`;

const Title = styled.h2<VariantCopyProps>`
  && {
    white-space: pre-line;
    padding: 0;
    margin: ${style.vars.space['3']} 0;
    color: ${({ color }) => (color ? color : 'initial')};
    font-size: ${({ sizeMobile }) => (sizeMobile ? sizeMobile : '1.5rem')};
    ${mediaQueries.mediumMin} {
      font-size: ${({ sizeDesktop }) => (sizeDesktop ? sizeDesktop : '2rem')};
    }
  }
`;

const SubTitle = styled.h4<VariantCopyProps>`
  && {
    white-space: pre-line;
    padding: 0;
    margin-top: 0;
    color: ${({ color }) => (color ? color : 'initial')};
    font-size: ${({ sizeMobile }) => (sizeMobile ? sizeMobile : '1rem')};
    ${mediaQueries.mediumMin} {
      font-size: ${({ sizeDesktop }) =>
        sizeDesktop ? sizeDesktop : '1.125rem'};
    }
  }
`;

type SplashButtonProps = React.ComponentProps<typeof Button>;

const SplashButton = (props: SplashButtonProps): ReactElement => {
  const isMobile = mediaQueries.smallMax.useMatchesBreakpoint();
  return (
    <Box mt={style.vars.space['2']} mb={style.vars.space['10']}>
      <Button {...props} fullWidth={props.fullWidth || isMobile} />
    </Box>
  );
};

const ImgWrapper = styled.img<{ width?: string; margin?: string }>`
  width: ${({ width }) => width || '100%'};
  margin: ${({ margin }) => margin || '0'};
`;

const titleImageSizes: Record<Sizes, string> = {
  large: '300px',
  medium: '250px',
  small: '80px'
};

const colors = {
  dark: style.colors.grey.darkest,
  light: style.colors.white
};

export const DynamicSplash: React.FC<Props> = ({
  splash,
  variantType
}): ReactElement => {
  const titleColor = colors[splash?.title.color || 'light'];
  const subtitleColor = colors[splash?.subtitle?.color || 'light'];

  return (
    <Column
      style={{
        backgroundImage: `${
          splash?.background.url ? `url(${splash.background.url})` : undefined
        }`,
        backgroundColor: style.colors.primaryBackground,
        backgroundSize: 'cover'
      }}
    >
      <WrapSplit>
        {(splash?.titleImage?.image.url || splash?.title) && (
          <Wrap>
            {splash.titleImage?.image && (
              <ImgWrapper
                data-testid="variantBackground"
                src={splash.titleImage.image.url}
                width={titleImageSizes[splash.titleImage.size || 'small']}
              ></ImgWrapper>
            )}
            {splash.title && (
              <Title color={titleColor}>{splash.title.text}</Title>
            )}
            {splash.subtitle && (
              <SubTitle color={subtitleColor}>{splash.subtitle.text}</SubTitle>
            )}
            {splash.cta && (
              <SplashButton
                variation={splash.cta.variant}
                url={splash.cta.href}
                onClick={() => {
                  trackEvent('Web Interaction', {
                    action: 'click',
                    link: 'External',
                    name: splash.cta?.text,
                    url: splash.cta?.href,
                    variantType
                  });
                }}
                external
              >
                {splash.cta.text}
              </SplashButton>
            )}
          </Wrap>
        )}
      </WrapSplit>
    </Column>
  );
};
